import { type MouseEvent, type ReactNode, useState } from 'react';
import {
  Close as CloseIcon,
  FiberManualRecord as StatusIcon,
  MoreVert as MoreIcon,
  PeopleAlt as PeopleIcon,
  PersonRemove as RemoveIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';

import type {
  ProviderWaitingRoomLink,
  WaitingRoomEntry,
  WaitingRoomProviderAvailability,
  WaitingRoomSseConnectionState,
} from '../types';
import { getWaitingRoomDarkChipSx } from '../utils';

import {
  WaitingRoomAvailabilityControl,
  WaitingRoomAvailabilityControlPresentation,
} from './WaitingRoomAvailabilityControl';
import { WaitingRoomEntryRow } from './WaitingRoomEntryRow';
import { WaitingRoomLinkButton } from './WaitingRoomLinkButton';

export interface WaitingRoomCallParticipant {
  id: string;
  name: string;
  subtitle?: string;
  avatarUrl?: string;
  queueEntryId?: string;
}

interface WaitingRoomPeoplePanelProps {
  entries: WaitingRoomEntry[];
  providerAvailability: WaitingRoomProviderAvailability;
  sseConnectionState?: WaitingRoomSseConnectionState;
  link?: ProviderWaitingRoomLink | null;
  inCallParticipants?: WaitingRoomCallParticipant[];
  variant?: 'light' | 'dark';
  density?: 'compact' | 'regular';
  title?: string;
  error?: string | null;
  maxVisibleEntries?: number;
  isSettingAvailability?: boolean;
  isCopyingLink?: boolean;
  linkCopied?: boolean;
  emptyLabel?: string;
  showAvailabilityToggle?: boolean;
  subjectLanguagePluralCap?: string;
  entryAction?: (entry: WaitingRoomEntry) => ReactNode;
  entryExpandedContent?: (entry: WaitingRoomEntry) => ReactNode;
  defaultExpandedEntryId?: string;
  /**
   * Container element for portaled MUI subcomponents (Menu, etc). Needed when
   * this panel renders inside a Document PiP window so popovers attach to the
   * PiP document rather than leaking back to the main `document.body`.
   */
  popoverContainerEl?: HTMLElement | null;
  onClose?: () => void;
  onSetAvailability?: (availability: WaitingRoomProviderAvailability) => void;
  onCopyLink?: () => void;
  onRemoveInCallParticipant?: (participant: WaitingRoomCallParticipant) => void;
}

const getConnectionLabel = (state?: WaitingRoomSseConnectionState) => {
  switch (state) {
    case 'open':
      return 'Live';
    case 'connecting':
      return 'Connecting';
    case 'reconnecting':
      return 'Reconnecting';
    case 'error':
      return 'Needs attention';
    default:
      return 'Idle';
  }
};

const getConnectionColor = (state?: WaitingRoomSseConnectionState) => {
  switch (state) {
    case 'open':
      return 'success' as const;
    case 'connecting':
    case 'reconnecting':
      return 'warning' as const;
    case 'error':
      return 'error' as const;
    default:
      return 'default' as const;
  }
};

const getParticipantInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase() || '?';

export function WaitingRoomPeoplePanel({
  entries,
  providerAvailability,
  sseConnectionState = 'idle',
  link,
  inCallParticipants = [],
  variant = 'light',
  density = 'regular',
  title = 'People',
  error,
  maxVisibleEntries,
  isSettingAvailability = false,
  isCopyingLink = false,
  linkCopied = false,
  emptyLabel = 'No one is waiting.',
  showAvailabilityToggle = true,
  subjectLanguagePluralCap,
  entryAction,
  entryExpandedContent,
  defaultExpandedEntryId,
  popoverContainerEl,
  onClose,
  onSetAvailability,
  onCopyLink,
  onRemoveInCallParticipant,
}: WaitingRoomPeoplePanelProps) {
  const isDark = variant === 'dark';
  const [inCallMenuAnchor, setInCallMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedInCallParticipantId, setSelectedInCallParticipantId] = useState<string | null>(
    null,
  );
  const visibleEntries = maxVisibleEntries ? entries.slice(0, maxVisibleEntries) : entries;
  const hiddenEntryCount = Math.max(0, entries.length - visibleEntries.length);
  const hasLink = Boolean(link?.short_url);
  const connectionColor = getConnectionColor(sseConnectionState);
  const selectedInCallParticipant = inCallParticipants.find(
    participant => participant.id === selectedInCallParticipantId,
  );
  const defaultOpenEntryId =
    defaultExpandedEntryId ??
    (inCallParticipants.length === 0 && entryExpandedContent
      ? visibleEntries[0]?.waiting_room_queue_entry_id
      : undefined);

  const handleOpenInCallMenu = (
    event: MouseEvent<HTMLButtonElement>,
    participant: WaitingRoomCallParticipant,
  ) => {
    setInCallMenuAnchor(event.currentTarget);
    setSelectedInCallParticipantId(participant.id);
  };

  const handleCloseInCallMenu = () => {
    setInCallMenuAnchor(null);
    setSelectedInCallParticipantId(null);
  };

  const handleRemoveInCallParticipant = () => {
    if (selectedInCallParticipant) {
      onRemoveInCallParticipant?.(selectedInCallParticipant);
    }
    handleCloseInCallMenu();
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isDark ? 'grey.900' : 'background.paper',
        color: isDark ? 'common.white' : 'text.primary',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
          <PeopleIcon sx={{ fontSize: 20, color: isDark ? 'grey.200' : 'text.secondary' }} />
          <Typography variant="h6" noWrap sx={{ fontSize: 18, fontWeight: 800 }}>
            {title}
          </Typography>
        </Stack>
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close people panel"
            sx={{ color: isDark ? 'grey.300' : 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Box sx={{ px: 2, pb: 1.5, flexShrink: 0 }}>
        {subjectLanguagePluralCap ? (
          <WaitingRoomAvailabilityControlPresentation
            availability={providerAvailability}
            isLoading={isSettingAvailability}
            variant={variant}
            showToggle={showAvailabilityToggle}
            subjectLanguagePluralCap={subjectLanguagePluralCap}
            onChange={onSetAvailability}
          />
        ) : (
          <WaitingRoomAvailabilityControl
            availability={providerAvailability}
            isLoading={isSettingAvailability}
            variant={variant}
            showToggle={showAvailabilityToggle}
            onChange={onSetAvailability}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pb: 2 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          {inCallParticipants.length > 0 && (
            <Box>
              <Typography
                variant="overline"
                sx={{
                  color: isDark ? 'grey.400' : 'text.secondary',
                  fontWeight: 800,
                  letterSpacing: 0,
                }}
              >
                In call with
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                {inCallParticipants.map(participant => (
                  <Stack
                    key={participant.id}
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                      px: 1,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: isDark ? 'grey.800' : 'action.hover',
                    }}
                  >
                    <Avatar
                      src={participant.avatarUrl}
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: 13,
                        fontWeight: 700,
                        bgcolor: isDark ? 'grey.700' : 'grey.200',
                        color: isDark ? 'common.white' : 'grey.700',
                      }}
                    >
                      {getParticipantInitials(participant.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ fontWeight: 700, color: isDark ? 'common.white' : 'text.primary' }}
                      >
                        {participant.name}
                      </Typography>
                      {participant.subtitle && (
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{ color: isDark ? 'grey.400' : 'text.secondary', display: 'block' }}
                        >
                          {participant.subtitle}
                        </Typography>
                      )}
                    </Box>
                    <StatusIcon sx={{ fontSize: 12, color: 'success.main' }} />
                    {onRemoveInCallParticipant && (
                      <IconButton
                        size="small"
                        aria-label={`Actions for ${participant.name}`}
                        onClick={event => handleOpenInCallMenu(event, participant)}
                        sx={{ color: isDark ? 'grey.300' : 'text.secondary' }}
                      >
                        <MoreIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
              <Menu
                anchorEl={inCallMenuAnchor}
                open={Boolean(inCallMenuAnchor)}
                onClose={handleCloseInCallMenu}
                container={popoverContainerEl ?? undefined}
              >
                <MenuItem onClick={handleRemoveInCallParticipant}>
                  <ListItemIcon>
                    <RemoveIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Remove</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          )}

          {inCallParticipants.length > 0 && (
            <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider' }} />
          )}

          <Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: isDark ? 'grey.400' : 'text.secondary',
                  fontWeight: 800,
                  letterSpacing: 0,
                }}
              >
                Waiting Room
              </Typography>
              <Chip
                label={getConnectionLabel(sseConnectionState)}
                color={connectionColor}
                variant="soft"
                size="xSmall"
                sx={isDark ? getWaitingRoomDarkChipSx(connectionColor) : undefined}
              />
            </Stack>

            {visibleEntries.length > 0 ? (
              <Stack spacing={0.75}>
                {visibleEntries.map(entry => (
                  <WaitingRoomEntryRow
                    key={entry.waiting_room_queue_entry_id}
                    entry={entry}
                    variant={variant}
                    density={density}
                    action={entryAction?.(entry)}
                    expandedContent={entryExpandedContent?.(entry)}
                    defaultExpanded={entry.waiting_room_queue_entry_id === defaultOpenEntryId}
                  />
                ))}
                {hiddenEntryCount > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: isDark ? 'grey.400' : 'text.secondary', px: 1 }}
                  >
                    {hiddenEntryCount} more waiting
                  </Typography>
                )}
              </Stack>
            ) : (
              <Box
                sx={{
                  px: 1.25,
                  py: 2,
                  borderRadius: 2,
                  bgcolor: isDark ? 'grey.800' : 'action.hover',
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: isDark ? 'grey.300' : 'text.secondary', fontWeight: 600 }}
                >
                  {emptyLabel}
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>

      {onCopyLink && (
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
            flexShrink: 0,
          }}
        >
          <WaitingRoomLinkButton
            hasLink={hasLink}
            isCopying={isCopyingLink}
            copied={linkCopied}
            variant={variant}
            onCopy={onCopyLink}
          />
        </Box>
      )}
    </Box>
  );
}
